import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Anestesikod = {
    id: number;
    code: string;
    displayName: string;
    valid_from_date: string;
    valid_to_date: string;
    avsnitt?: string;
    beskrivning?: string;
    kapitel?: string;
    klassdel?: number;
};

export type AnestesikodDisplay = {
    value: Anestesikod["id"];
    label: string;
};

type State = BaseState & {
    anestesikoder: Map<Anestesikod["id"], Anestesikod>;
};

export const convertAnestesikodData: (args: { anestesikod?: Anestesikod }) => AnestesikodDisplay | null = ({
    anestesikod,
}) => {
    if (!anestesikod) {
        return null;
    }

    return {
        value: anestesikod.id,
        label: `${anestesikod.code} ${anestesikod.displayName}`,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        anestesikoder: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "anestesikoder";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        anestesikoder: (state: State) => [...state.anestesikoder.values()],
        anestesikodSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertAnestesikodData({ anestesikod: singleResult }));
            }

            return result;
        },
        anestesikodById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.anestesikoder.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addAnestesikoder: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                anestesikoder = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                anestesikoder: Anestesikod[];
            }
        ) =>
            subsetSetter<State, Anestesikod>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: anestesikoder,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchAnestesikoder: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Anestesikod>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/anestesikoder/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addAnestesikoder", {
                    subsetName,
                    offset: resultOffset,
                    anestesikoder: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertAnestesikodData({ anestesikod: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
